import { useMutation, useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useCreatorCampaigns = (creatorId?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["creator-campaigns", scope.merchantId, scope.workspaceId, creatorId],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns.creator[":creatorId"].$get({
            param: { ...scope.param, creatorId: creatorId as string },
          })
        : await client.merchant[":merchantId"].campaigns.creator[":creatorId"].$get({
            param: { ...scope.param, creatorId: creatorId as string },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    enabled: !!scope.merchantId && !!creatorId,
  });
};

export const useRemoveCreatorFromCampaign = (creatorId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ campaignId }: { campaignId: string }) => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns[":campaignId"].creator[
            ":creatorId"
          ].$delete({
            param: { ...scope.param, campaignId, creatorId },
          })
        : await client.merchant[":merchantId"].campaigns[":campaignId"].creator[":creatorId"].$delete({
            param: { ...scope.param, campaignId, creatorId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-campaigns", scope.merchantId, creatorId] });
      queryClient.invalidateQueries({ queryKey: ["creator", scope.merchantId, creatorId] });
    },
  });
};

export const useRemoveCreatorFromAllCampaigns = (creatorId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async () => {
      if (!scope.merchantId) throw new Error("Missing merchant context");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].campaigns.creator[":creatorId"].all.$delete({
            param: { ...scope.param, creatorId },
          })
        : await client.merchant[":merchantId"].campaigns.creator[":creatorId"].all.$delete({
            param: { ...scope.param, creatorId },
          });
      if (!res.ok) throw await handleError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creator-campaigns", scope.merchantId, creatorId] });
      queryClient.invalidateQueries({ queryKey: ["creator", scope.merchantId, creatorId] });
    },
  });
};
